import { API_BASE_URL } from '../config/serverApiConfig';
import errorHandler from '../request/errorHandler';
import { api } from './api';
import type { AccessLevel } from '../config/capabilities';
import { managedByFrom409, type ManagedBy } from './delegation.service';

export type { AccessLevel };

export interface SelfCapabilities {
  workspace_id: number;
  role: string | null;
  custom_role_id: number | null;
  capabilities: Record<string, AccessLevel>;
}

/**
 * The caller's own resolved row for this workspace — gated on membership, so every role
 * can call it. `GET /r/v2/workspaces/{id}/capabilities` (the full matrix, every role's row)
 * stays ROLE_CONFIG-gated and is for the admin configuration screen, not for UI gating.
 */
export const getSelfCapabilities = async (
  workspaceId: number
): Promise<SelfCapabilities> => {
  const response = await api().get(
    `${API_BASE_URL}r/v2/workspaces/${workspaceId}/capabilities/me`
  );
  return response.data;
};

/* ------------------------------------------------------------------ *
 * The ROLE_CONFIG surface — the capability grid in Settings → Roles.
 * ------------------------------------------------------------------ */

/** `role → module → access`. Keys are whatever the workspace actually has. */
export type CapabilityMatrix = Record<string, Record<string, AccessLevel>>;

export interface WorkspaceCapabilities {
  workspace_id: number;
  display_path: string;
  /** Modules the ceiling refuses to configure. Read this; never hardcode the set. */
  immutable_modules: string[];
  /**
   * Every custom role this workspace defines, carried in the *same* response as the
   * matrix. The matrix keys custom subjects `custom:{id}` and carries no names, so this
   * array is what labels those columns — reading names from a second request would show
   * the wrong label whenever a concurrent rename lands between the two.
   */
  custom_roles: CustomRole[];
  matrix: CapabilityMatrix;
  /** Who owns `roles` for this node when it is not this node; null when it does. */
  managed_by?: ManagedBy | null;
}

/**
 * The whole grid: code defaults with this workspace's overrides already merged. The
 * response does not distinguish an overridden cell from one sitting at its default, and
 * there is no route that clears an override, so the grid does not claim to either.
 *
 * Read uncached — `api()` installs axios-cache-interceptor, and a save immediately
 * followed by a stale re-read would show an operator their change being undone.
 */
export const getWorkspaceCapabilities = async (
  workspaceId: number
): Promise<WorkspaceCapabilities> => {
  const response = await api().get(
    `${API_BASE_URL}r/v2/workspaces/${workspaceId}/capabilities`,
    { cache: false } as any
  );
  return response.data;
};

/** One entry of the 422 body. `module` is absent on subject-level refusals. */
export interface CapabilityViolation {
  code: string;
  module?: string | null;
  detail?: string | null;
}

/**
 * One subject and the cells to set for it. Exactly one of `role` / `custom_role_id` —
 * a custom role is addressed by id, never by name.
 */
export interface OverrideWrite {
  role?: string | null;
  custom_role_id?: number | null;
  cells: Record<string, AccessLevel>;
}

export type PutCapabilitiesResult =
  | { ok: true }
  | {
      ok: false;
      violations: CapabilityViolation[];
      /**
       * The `custom_role_id` is not a role of this workspace — it was deleted, or it
       * belongs to another workspace. The caller's copy of the matrix is stale; re-read
       * it rather than reporting the write as a capability refusal.
       */
      notFound?: boolean;
      message?: string;
      /** 409 SETTING_MANAGED_ELSEWHERE: the node does not own `roles`. */
      managedBy?: ManagedBy | null;
      /** 403 GRANT_EXCEEDS_GRANTOR: the modules the grantor's ceiling refused. */
      exceeds?: string[];
    };

/** The module list of a 403 GRANT_EXCEEDS_GRANTOR, or null for any other error. */
export const grantExceedsFrom403 = (error: any): string[] | null => {
  const detail = error?.response?.data?.detail;
  if (error?.response?.status !== 403 || detail?.code !== 'GRANT_EXCEEDS_GRANTOR') return null;
  return Array.isArray(detail.modules) ? detail.modules : [];
};

const isViolationList = (detail: unknown): detail is CapabilityViolation[] =>
  Array.isArray(detail) && detail.every((d) => d && typeof d === 'object' && 'code' in d);

/**
 * The server's own wording for a refusal. It is the only thing that separates "that name
 * is taken" from "this workspace is at its 16-role limit" — both come back as 409 — so it
 * is shown verbatim rather than being replaced by a guess about which case it was.
 */
const serverMessage = (data: any): string | null => {
  if (typeof data?.detail === 'string') return data.detail.trim() || null;
  if (Array.isArray(data?.detail)) {
    const joined = data.detail
      .map((entry: any) => entry?.msg ?? entry?.detail)
      .filter((text: unknown): text is string => typeof text === 'string' && text.trim() !== '')
      .join('\n');
    return joined || null;
  }
  if (typeof data?.message === 'string') return data.message.trim() || null;
  return null;
};

/**
 * Set one subject's access to one or more modules.
 *
 * A refusal comes back as 422 carrying the **full** violation list, not the first
 * failure, and those objects have `code`/`module`/`detail` and no `msg` — which is
 * exactly what the shared `errorHandler` maps over, so routing them through it renders
 * a notification reading "undefined". They are returned structured instead, for the grid
 * to anchor to its own cells. Every other failure keeps the shared handler.
 */
export const putWorkspaceCapabilities = async (
  workspaceId: number,
  body: OverrideWrite
): Promise<PutCapabilitiesResult> => {
  try {
    await api().put(
      `${API_BASE_URL}r/v2/workspaces/${workspaceId}/capabilities`,
      body
    );
    return { ok: true };
  } catch (error: any) {
    const { status, data } = error?.response ?? {};
    const managedBy = managedByFrom409(error);
    if (managedBy) return { ok: false, violations: [], managedBy };
    const exceeds = grantExceedsFrom403(error);
    if (exceeds) return { ok: false, violations: [], exceeds };
    if (status === 422 && isViolationList(data?.detail)) {
      return { ok: false, violations: data.detail };
    }
    // A `custom_role_id` from another workspace — or one deleted under the open grid —
    // is a 404, not a 422. It says nothing about the cells, so it is reported as a stale
    // subject instead of being rendered as a refused change.
    if (status === 404) {
      return {
        ok: false,
        violations: [],
        notFound: true,
        message: serverMessage(data) ?? undefined
      };
    }
    errorHandler(error);
    return { ok: false, violations: [] };
  }
};

/* ------------------------------------------------------------------ *
 * Custom roles — a collection per workspace, tier 2, never inherited.
 * ------------------------------------------------------------------ */

export interface CustomRole {
  id: number;
  workspace_id: number;
  name: string;
  description: string | null;
  /** The node that defines the role. Roles from above are read-only on this node. */
  defined_at?: ManagedBy;
}

export interface CustomRoleWrite {
  name: string;
  description?: string | null;
}

export interface CustomRoleFailure {
  /** HTTP status, or 0 when the request never reached the server. */
  status: number;
  /** The server's own wording, which is what tells the two 409 cases apart. */
  message: string;
  /** True when the shared handler already raised a notification — don't repeat it. */
  notified: boolean;
  /** 409 SETTING_MANAGED_ELSEWHERE: the node does not own `roles`. */
  managedBy?: ManagedBy | null;
}

export type CustomRoleResult<T> =
  | { ok: true; data: T }
  | { ok: false; error: CustomRoleFailure };

/**
 * The refusals this surface has a real answer for: 409 (name taken, or the per-workspace
 * limit reached, or members still hold the role), 404 (no such role *in this workspace*)
 * and 400 (blank name, or one shadowing a builtin). Each is reported inline against the
 * form or row that caused it; everything else keeps the shared notification handler.
 */
const ANSWERED_STATUSES = new Set([400, 404, 409]);

const asFailure = (error: any, fallback: string): { ok: false; error: CustomRoleFailure } => {
  const status: number = error?.response?.status ?? 0;
  const message = serverMessage(error?.response?.data) ?? fallback;
  const managedBy = managedByFrom409(error);
  if (managedBy) return { 
    ok: false, 
    error: { status, message, notified: false, managedBy } 
  };
  const exceeds = grantExceedsFrom403(error);
  if (exceeds) {
    return {
      ok: false,
      error: {
        status,
        message: `You can't grant access beyond your own: ${exceeds.join(', ')}.`,
        notified: false
      }
    };
  }
  if (ANSWERED_STATUSES.has(status)) {
    return { ok: false, error: { status, message, notified: false } };
  }
  errorHandler(error);
  return { ok: false, error: { status, message, notified: true } };
};

/**
 * Every custom role in the workspace, newest-first ordering left to the server.
 *
 * Read uncached and never carried across a workspace switch: names are unique only inside
 * a workspace, so an "Auditor" here and an "Auditor" next door are different roles with
 * different capabilities, and a reused list would silently address the wrong one.
 */
export const listCustomRoles = async (workspaceId: number): Promise<CustomRole[]> => {
  const response = await api().get(
    `${API_BASE_URL}r/v2/workspaces/${workspaceId}/custom-roles`,
    { cache: false } as any
  );
  const data = response.data;
  return Array.isArray(data) ? data : (data?.custom_roles ?? []);
};

/** One role by id. 404 when the id is not a role of *this* workspace. */
export const getCustomRole = async (
  workspaceId: number,
  customRoleId: number
): Promise<CustomRole> => {
  const response = await api().get(
    `${API_BASE_URL}r/v2/workspaces/${workspaceId}/custom-roles/${customRoleId}`,
    { cache: false } as any
  );
  return response.data;
};

/**
 * A created role can do **nothing** until capability rows are written for it — creating
 * it and granting it modules are two separate calls, so callers must lead the operator
 * into the grid rather than let them believe the role is usable.
 *
 * 409 is either a name already taken in this workspace or the workspace being at its
 * role limit; 400 is a blank name or one shadowing a builtin.
 */
export const createCustomRole = async (
  workspaceId: number,
  payload: CustomRoleWrite
): Promise<CustomRoleResult<CustomRole>> => {
  try {
    const response = await api().post(
      `${API_BASE_URL}r/v2/workspaces/${workspaceId}/custom-roles`,
      payload
    );
    return { ok: true, data: response.data };
  } catch (error) {
    return asFailure(error, 'The role could not be created.');
  }
};

export const updateCustomRole = async (
  workspaceId: number,
  customRoleId: number,
  payload: CustomRoleWrite
): Promise<CustomRoleResult<CustomRole>> => {
  try {
    const response = await api().put(
      `${API_BASE_URL}r/v2/workspaces/${workspaceId}/custom-roles/${customRoleId}`,
      payload
    );
    return { ok: true, data: response.data };
  } catch (error) {
    return asFailure(error, 'The role could not be updated.');
  }
};

/**
 * Refused with 409 while anyone still holds the role; the server's message names the
 * holder count, and the caller turns that into "reassign those members first".
 */
export const deleteCustomRole = async (
  workspaceId: number,
  customRoleId: number
): Promise<CustomRoleResult<null>> => {
  try {
    await api().delete(
      `${API_BASE_URL}r/v2/workspaces/${workspaceId}/custom-roles/${customRoleId}`
    );
    return { ok: true, data: null };
  } catch (error) {
    return asFailure(error, 'The role could not be deleted.');
  }
};
